import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { deliveryNoteService, invoiceService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';

const DeliveryNotesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 0.5rem;
  overflow-x: hidden;

  @media (min-width: 768px) {
    padding: 0 1rem;
  }

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
  }

  @media (min-width: 1024px) {
    padding: 0;
  }
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 2rem;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  @media (min-width: 1024px) {
    gap: 1rem;
  }
`;

const Button = styled.button`
  padding: 0.5rem 0.75rem;
  background-color: ${props => props.$variant === 'primary' ? '#4CAF50' : '#2196F3'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.$variant === 'primary' ? '#45a049' : '#1976D2'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (min-width: 1024px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const SearchBar = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  width: 100%;

  @media (min-width: 768px) {
    width: 300px;
  }
`;

const TableWrapper = styled.div`
  display: none;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (min-width: 768px) {
    display: block;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #dee2e6;
  font-size: 0.85rem;
  white-space: nowrap;

  @media (min-width: 1024px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

const TableRow = styled.tr`
  &:hover {
    background-color: #f8f9fa;
  }
`;

const TableCell = styled.td`
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.85rem;

  @media (min-width: 1024px) {
    padding: 1rem;
    font-size: 1rem;
  }
`;

// Mobile Card Components
const MobileCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  border-left: 4px solid ${props => {
    switch(props.$status) {
      case 'draft': return '#6c757d';
      case 'confirmed': return '#ffc107';
      case 'delivered': return '#4CAF50';
      case 'partial_invoiced': return '#ff9800';
      case 'invoiced': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  }};
`;

const MobileCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const MobileCardNumber = styled.div`
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MobileCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
`;

const MobileCardLabel = styled.span`
  color: #666;
`;

const MobileCardValue = styled.span`
  color: #333;
  font-weight: 500;
`;

const MobileCardActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
  flex-wrap: wrap;
`;

const MobileActionButton = styled.button`
  padding: 0.5rem 0.75rem;
  background: ${props => props.$variant === 'primary' ? '#28a745' : props.$variant === 'secondary' ? '#17a2b8' : '#007bff'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;

const MobileCheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.$status) {
      case 'draft': return '#6c757d20';
      case 'confirmed': return '#ffc10720';
      case 'delivered': return '#4CAF5020';
      case 'partial_invoiced': return '#ff980020';
      case 'invoiced': return '#28a74520';
      case 'cancelled': return '#dc354520';
      default: return '#6c757d20';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'draft': return '#6c757d';
      case 'confirmed': return '#ffc107';
      case 'delivered': return '#4CAF50';
      case 'partial_invoiced': return '#ff9800';
      case 'invoiced': return '#28a745';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  }};
`;

const LinesInfo = styled.div`
  font-size: 0.875rem;
  color: #666;
  max-height: 60px;
  overflow-y: auto;
`;

const ActionLink = styled(Link)`
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
  margin-right: 1rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const Checkbox = styled.input`
  width: 1.2rem;
  height: 1.2rem;
  cursor: pointer;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

// Pagination Components
const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding: 1rem 0;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    margin-top: 2rem;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.9rem;
  color: #666;
  order: 2;

  @media (min-width: 768px) {
    order: 1;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  order: 1;

  @media (min-width: 768px) {
    order: 2;
  }
`;

const PageButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid ${props => props.$active ? '#2196F3' : '#ddd'};
  background: ${props => props.$active ? '#2196F3' : 'white'};
  color: ${props => props.$active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  min-width: 40px;

  &:hover:not(:disabled) {
    background: ${props => props.$active ? '#1976D2' : '#f5f5f5'};
  }

  &:disabled {
    background: #f5f5f5;
    color: #ccc;
    cursor: not-allowed;
    border-color: #eee;
  }
`;

const PageSizeSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
`;

const formatDeliveryStatus = (status) => {
  const statusMap = {
    draft: 'Borrador',
    confirmed: 'Confirmado',
    delivered: 'Entregado',
    partial_invoiced: 'Parc. Facturado',
    invoiced: 'Facturado',
    cancelled: 'Cancelado'
  };
  return statusMap[status] || status;
};

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const DeliveryNotes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const queryClient = useQueryClient();

  // Helper function to round to 5 cents (for TPV orders)
  const roundToFiveCents = (number) => {
    return Math.ceil(number * 20) / 20;
  };

  // Helper to determine if an order was created with Calculadora Profesional
  const isCalculadoraPro = (fingerprint) => {
    if (!fingerprint) return false;
    // CalculatoraPro orders have fingerprint starting with INT- or EXT-
    return fingerprint.startsWith('INT-') || fingerprint.startsWith('EXT-');
  };

  // Helper to calculate final amount with correct rounding based on delivery note type
  const calculateDisplayFinalAmount = (deliveryNote) => {
    if (!deliveryNote) return 0;
    const finalAmount = parseFloat(deliveryNote.totalAmount || 0);

    // Check if ALL lines are from CalculadoraPro
    // If any line is from TPV, apply rounding to total
    const allLinesAreCalculadoraPro = deliveryNote.lines?.every(line =>
      line.orderSnapshot?.fingerprint && isCalculadoraPro(line.orderSnapshot.fingerprint)
    );

    if (allLinesAreCalculadoraPro) {
      // CalculadoraPro orders - round BASE IMPONIBLE to 5 cents, then add VAT
      const baseImponible = finalAmount / 1.21;
      const baseRedondeada = roundToFiveCents(baseImponible);
      const iva = baseRedondeada * 0.21;
      return baseRedondeada + iva;
    } else {
      // Has TPV orders - round TOTAL (with VAT) to 5 cents
      return roundToFiveCents(finalAmount);
    }
  };

  const { data: deliveryNotes, isLoading, error } = useQuery({
    queryKey: ['deliveryNotes'],
    queryFn: () => deliveryNoteService.getAll(),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => deliveryNoteService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['deliveryNotes']);
      toast.success('Estado actualizado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Error al actualizar estado');
    }
  });

  const filteredNotes = deliveryNotes?.filter(note => {
    const search = searchTerm.toLowerCase();
    const clientName = note.client?.name || '';
    return (
      note.deliveryNumber?.toString().includes(search) ||
      clientName.toLowerCase().includes(search) ||
      formatDeliveryStatus(note.status).toLowerCase().includes(search)
    );
  }) || [];

  // Pagination logic
  const totalItems = filteredNotes.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedNotes = filteredNotes.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedNotes(filteredNotes.map(note => note.id));
    } else {
      setSelectedNotes([]);
    }
  };

  const handleSelectNote = (noteId) => {
    setSelectedNotes(prev => 
      prev.includes(noteId) 
        ? prev.filter(id => id !== noteId)
        : [...prev, noteId]
    );
  };

  const handleCreateInvoices = async () => {
    console.log('handleCreateInvoices called');
    console.log('selectedNotes:', selectedNotes);

    try {
      // Get all line IDs from selected delivery notes
      const selectedDeliveryNotes = deliveryNotes.filter(note =>
        selectedNotes.includes(note.id)
      );

      console.log('selectedDeliveryNotes:', selectedDeliveryNotes);

      // Extract all line IDs that haven't been invoiced yet
      const deliveryLineIds = [];
      selectedDeliveryNotes.forEach(note => {
        if (note.lines) {
          note.lines.forEach(line => {
            if (!line.isInvoiced) {
              deliveryLineIds.push(line.id);
            }
          });
        }
      });

      console.log('deliveryLineIds:', deliveryLineIds);

      if (deliveryLineIds.length === 0) {
        toast.warning('No hay líneas pendientes de facturar en los albaranes seleccionados');
        return;
      }

      // Call the API to create invoices from delivery lines
      console.log('Calling invoiceService.createFromDeliveryLines with:', deliveryLineIds);
      const response = await invoiceService.createFromDeliveryLines(deliveryLineIds);

      console.log('Invoice created successfully:', response);

      toast.success(`Factura generada correctamente: FAC${response.invoice?.invoiceNumber || ''}`);

      // Refresh the delivery notes list
      queryClient.invalidateQueries(['deliveryNotes']);

      // Clear selection
      setSelectedNotes([]);

    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.error || 'Error al generar la factura');
    }
  };

  const handleStatusChange = (noteId, newStatus) => {
    updateStatusMutation.mutate({ id: noteId, status: newStatus });
  };

  if (isLoading) {
    return (
      <DeliveryNotesContainer>
        <LoadingState>Cargando albaranes...</LoadingState>
      </DeliveryNotesContainer>
    );
  }

  if (error) {
    // Show the actual error message from the API
    const errorMessage = error.response?.data?.error || error.message || 'Error al cargar los albaranes';

    // Special handling for authentication errors
    if (error.response?.status === 401) {
      toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
    } else {
      toast.error(errorMessage);
    }

    return (
      <DeliveryNotesContainer>
        <EmptyState>{errorMessage}</EmptyState>
      </DeliveryNotesContainer>
    );
  }

  return (
    <DeliveryNotesContainer>
      <Header>
        <Title>Albaranes</Title>
        <ActionButtons>
          <SearchBar
            type="text"
            placeholder="Buscar por número, cliente..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <Button
            $variant="primary"
            onClick={() => window.location.href = '/orders?deliverable=true'}
          >
            Crear desde Pedidos
          </Button>
          <Button
            $variant="secondary"
            disabled={selectedNotes.length === 0}
            onClick={handleCreateInvoices}
          >
            Facturar Seleccionados
          </Button>
        </ActionButtons>
      </Header>

      {filteredNotes.length === 0 ? (
        <EmptyState>
          {searchTerm ? 'No se encontraron albaranes' : 'No hay albaranes registrados'}
        </EmptyState>
      ) : (
        <>
        {/* Mobile Card View */}
        <MobileCardList>
          {paginatedNotes.map(note => {
            const canInvoice = ['confirmed', 'delivered', 'partial_invoiced'].includes(note.status);
            const hasUninvoicedLines = note.lines?.some(line => !line.isInvoiced);

            return (
              <MobileCard key={note.id} $status={note.status}>
                <MobileCardHeader>
                  <MobileCheckboxRow>
                    <Checkbox
                      type="checkbox"
                      checked={selectedNotes.includes(note.id)}
                      onChange={() => handleSelectNote(note.id)}
                      disabled={!canInvoice || !hasUninvoicedLines}
                    />
                    <MobileCardNumber>
                      ALB{note.deliveryNumber}
                      {note.isImportant && (
                        <span style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          backgroundColor: '#dc3545',
                          borderRadius: '50%',
                          boxShadow: '0 0 6px rgba(220, 53, 69, 0.6)'
                        }}
                        title="Albarán urgente"
                        />
                      )}
                    </MobileCardNumber>
                  </MobileCheckboxRow>
                  <StatusBadge $status={note.status}>
                    {formatDeliveryStatus(note.status)}
                  </StatusBadge>
                </MobileCardHeader>

                <MobileCardBody>
                  <MobileCardRow>
                    <MobileCardLabel>Cliente</MobileCardLabel>
                    <MobileCardValue>{note.client?.name || 'Sin cliente'}</MobileCardValue>
                  </MobileCardRow>
                  <MobileCardRow>
                    <MobileCardLabel>Fecha</MobileCardLabel>
                    <MobileCardValue>{formatDate(note.deliveryDate)}</MobileCardValue>
                  </MobileCardRow>
                  <MobileCardRow>
                    <MobileCardLabel>Total</MobileCardLabel>
                    <MobileCardValue>{formatCurrency(calculateDisplayFinalAmount(note))}</MobileCardValue>
                  </MobileCardRow>
                  <MobileCardRow>
                    <MobileCardLabel>Items</MobileCardLabel>
                    <MobileCardValue>{note.lines?.length || 0} líneas</MobileCardValue>
                  </MobileCardRow>
                </MobileCardBody>

                <MobileCardActions>
                  <MobileActionButton as={Link} to={`/delivery-notes/${note.id}`}>
                    Ver Detalle
                  </MobileActionButton>
                  {note.status === 'draft' && (
                    <MobileActionButton
                      $variant="primary"
                      onClick={() => handleStatusChange(note.id, 'confirmed')}
                    >
                      Confirmar
                    </MobileActionButton>
                  )}
                  {note.status === 'confirmed' && (
                    <MobileActionButton
                      $variant="secondary"
                      onClick={() => handleStatusChange(note.id, 'delivered')}
                    >
                      Marcar Entregado
                    </MobileActionButton>
                  )}
                </MobileCardActions>
              </MobileCard>
            );
          })}
        </MobileCardList>

        {/* Desktop Table View */}
        <TableWrapper>
        <Table>
          <thead>
            <tr>
              <TableHeader>
                <Checkbox
                  type="checkbox"
                  checked={selectedNotes.length === paginatedNotes.length && paginatedNotes.length > 0}
                  onChange={handleSelectAll}
                />
              </TableHeader>
              <TableHeader>Número</TableHeader>
              <TableHeader>Fecha</TableHeader>
              <TableHeader>Cliente</TableHeader>
              <TableHeader>Items</TableHeader>
              <TableHeader>Total</TableHeader>
              <TableHeader>Estado</TableHeader>
              <TableHeader>Acciones</TableHeader>
            </tr>
          </thead>
          <tbody>
            {paginatedNotes.map(note => {
              const canInvoice = ['confirmed', 'delivered', 'partial_invoiced'].includes(note.status);
              const hasUninvoicedLines = note.lines?.some(line => !line.isInvoiced);

              return (
                <TableRow key={note.id}>
                  <TableCell>
                    <Checkbox
                      type="checkbox"
                      checked={selectedNotes.includes(note.id)}
                      onChange={() => handleSelectNote(note.id)}
                      disabled={!canInvoice || !hasUninvoicedLines}
                    />
                  </TableCell>
                  <TableCell>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      ALB{note.deliveryNumber}
                      {note.isImportant && (
                        <span style={{
                          display: 'inline-block',
                          width: '6px',
                          height: '6px',
                          backgroundColor: '#dc3545',
                          borderRadius: '50%',
                          boxShadow: '0 0 6px rgba(220, 53, 69, 0.6)'
                        }}
                        title="Albarán urgente"
                        />
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{formatDate(note.deliveryDate)}</TableCell>
                  <TableCell>{note.client?.name || 'Sin cliente'}</TableCell>
                  <TableCell>
                    <LinesInfo>
                      {note.lines?.map(line => (
                        <div key={line.id}>
                          {line.quantity}x {line.product?.name || line.description}
                          {line.isInvoiced && ' (F)'}
                        </div>
                      ))}
                    </LinesInfo>
                  </TableCell>
                  <TableCell>{formatCurrency(calculateDisplayFinalAmount(note))}</TableCell>
                  <TableCell>
                    <StatusBadge $status={note.status}>
                      {formatDeliveryStatus(note.status)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>
                    <ActionLink to={`/delivery-notes/${note.id}`}>
                      Ver
                    </ActionLink>
                    {note.status === 'draft' && (
                      <button
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          color: '#28a745',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onClick={() => handleStatusChange(note.id, 'confirmed')}
                      >
                        Confirmar
                      </button>
                    )}
                    {note.status === 'confirmed' && (
                      <button
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#17a2b8',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onClick={() => handleStatusChange(note.id, 'delivered')}
                      >
                        Marcar como entregado
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
        </TableWrapper>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationInfo>
              Mostrando {startIndex + 1}-{Math.min(endIndex, totalItems)} de {totalItems} albaranes
            </PaginationInfo>
            <PaginationControls>
              <PageSizeSelect value={itemsPerPage} onChange={handleItemsPerPageChange}>
                {ITEMS_PER_PAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option} por página</option>
                ))}
              </PageSizeSelect>
              <PageButton
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ←
              </PageButton>
              {getPageNumbers().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} style={{ padding: '0 0.5rem', color: '#666' }}>...</span>
                ) : (
                  <PageButton
                    key={page}
                    $active={currentPage === page}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </PageButton>
                )
              ))}
              <PageButton
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                →
              </PageButton>
            </PaginationControls>
          </PaginationContainer>
        )}
        </>
      )}
    </DeliveryNotesContainer>
  );
};

export default DeliveryNotes;
